/**
 * <security-audit-change-line .lead=${description} .changes=${changes}>
 *
 * A security audit edit entry's line, and the card of what it changed (D-151 rule 5, amended). The
 * architect, on the line that spelled out every value: "it would be better to indicate what changed
 * in the line, and a hover would show all the details in a nice before after label pair view".
 *
 * The line is the entry's lead (kept text: terms resolved, a person as their name with their card),
 * then ": " and the changed fields' labels joined by ", " ("Phone number, Address"). The labels are
 * the card's anchor: hovering them, reaching them with the keyboard, or tapping them opens a card
 * listing each change as its label, the value before and the value after. The labels rather than
 * the whole line anchor it because a person-edit lead holds a <user-name>, itself a control with a
 * card of its own; a control inside a control would give one press two cards.
 *
 * The card is drawn in the top layer by <agwa-hover-card>, the same primitive as the person card,
 * with the same open and close behaviour, so no row of the log paints over it. Its content is
 * rendered only while open, so reading the row's text finds the line alone.
 *
 * An entry with no changes (any other activity, or an edit written before changes were kept) is
 * the lead alone, as the page showed it before.
 */
import { LitElement, html, nothing, unsafeCSS, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { SecurityAuditFieldChangeDto } from '../../api/agwa-api-model';
import { renderKeptText } from '../../language/kept-text';
import {
  securityAuditFieldLabel,
  securityAuditValueIsKept,
  securityAuditValueLabel,
} from './security-audit-field-labels';
import type { AuditFieldChange } from '../../components/audit/audit-entry';
import '../../components/audit/agwa-audit-changes';
import '../../components/hover-card/agwa-hover-card';
import type { HoverCardToggleDetail } from '../../components/hover-card/agwa-hover-card';
import changeLineStyles from './security-audit-change-line.css?inline';

/**
 * A kept value drawn line by line: a value holding more than one line (the iMIS details' pairs, a
 * description with line breaks) keeps them. Each line goes through renderKeptText only when the
 * value is server-written (`kept`, securityAuditValueIsKept); a typed value is drawn as stored, so
 * its braces are never read as a tag (#398 C10).
 */
function renderLines(value: string, kept: boolean): TemplateResult {
  return html`${value
    .split('\n')
    .map(
      (line, i) =>
        html`${i > 0 ? html`<br />` : nothing}${kept ? renderKeptText(line) : line}`
    )}`;
}

@customElement('security-audit-change-line')
export class SecurityAuditChangeLine extends LitElement {
  static styles = [unsafeCSS(changeLineStyles)];

  /** The entry's description: the lead sentence the server kept, with its tags. */
  @property({ type: String }) lead = '';

  /** The fields the edit changed, in the order the server kept them; empty for none. */
  @property({ attribute: false }) changes: SecurityAuditFieldChangeDto[] = [];

  /** Whether the card is showing. */
  @state() private open = false;

  private onToggle = (e: CustomEvent<HoverCardToggleDetail>) => {
    this.open = e.detail.open;
  };

  /**
   * The card: each change as its label, the value before and the value after, drawn by the one
   * before-and-after view the record trails use (<agwa-audit-changes>), so "no value" reads the same
   * everywhere ("not set" / "cleared"). Values are put in the page's words first
   * (securityAuditValueLabel), then drawn line by line, the iMIS details one pair per line, and
   * through renderKeptText only for the Owner change, which holds person references
   * (securityAuditValueIsKept); every other value is drawn as typed.
   */
  private renderCard() {
    const changes: AuditFieldChange[] = this.changes.map((change) => ({
      fieldName: change.field,
      label: securityAuditFieldLabel(change.field),
      oldValue: securityAuditValueLabel(change.field, change.old),
      newValue: securityAuditValueLabel(change.field, change.new),
      keptText: securityAuditValueIsKept(change.field),
    }));
    return html`<div
      id="changes-card"
      class="changes-card"
      role="group"
      aria-label="What changed"
    >
      <agwa-audit-changes
        .changes=${changes}
        .valueTemplate=${renderLines}
      ></agwa-audit-changes>
    </div>`;
  }

  render() {
    const lead = renderKeptText(this.lead);
    if (this.changes.length === 0) return lead;
    const labels = this.changes
      .map((change) => securityAuditFieldLabel(change.field))
      .join(', ');
    const card = this.open ? this.renderCard() : nothing;
    // No whitespace between the tags: the labels follow ": " in running text, where a stray text
    // node would render as a space.
    return html`${lead}:
      <agwa-hover-card @hover-card-toggle=${this.onToggle}
        ><button
          slot="anchor"
          type="button"
          class="changed-fields"
          aria-expanded=${this.open ? 'true' : 'false'}
          aria-controls=${this.open ? 'changes-card' : nothing}
        >
          ${labels}</button
        >${card}</agwa-hover-card
      >`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'security-audit-change-line': SecurityAuditChangeLine;
  }
}
